import { useState, type CSSProperties } from 'react'
import DashAvatar from '../../components/ui/DashAvatar'
import DashIcon from '../../components/ui/DashIcon'

interface Toggles {
  autoAssign: boolean
  surge:      boolean
  sms:        boolean
  email:      boolean
  push:       boolean
  cod:        boolean
  realtime:   boolean
  auditLog:   boolean
  mfa:        boolean
  sandbox:    boolean
}

const sections = [
  { id: 'general',       label: 'Général',        icon: 'cog'    },
  { id: 'operations',    label: 'Opérations',      icon: 'truck'  },
  { id: 'pricing',       label: 'Pricing & SLA',   icon: 'wallet' },
  { id: 'notifications', label: 'Notifications',   icon: 'bell'   },
  { id: 'roles',         label: 'Équipe & rôles',  icon: 'users'  },
  { id: 'integrations',  label: 'Intégrations',    icon: 'globe'  },
  { id: 'security',      label: 'Sécurité',        icon: 'shield' },
  { id: 'localization',  label: 'Localisation',    icon: 'globe'  },
  { id: 'branding',      label: 'Identité',        icon: 'star'   },
  { id: 'billing',       label: 'Facturation',     icon: 'card'   },
]

function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return <div className={`tgl${on ? ' on' : ''}`} onClick={onClick} />
}

export default function AdminSettings() {
  const [page, setPage] = useState('general')
  const [tgls, setTgls] = useState<Toggles>({
    autoAssign: true, surge: true, sms: true, email: true, push: true,
    cod: true, realtime: true, auditLog: true, mfa: false, sandbox: false,
  })
  const t = (k: keyof Toggles) => setTgls(prev => ({ ...prev, [k]: !prev[k] }))

  const integrations = [
    ['Stripe',      'Paiements & virements',      'Connecté',      'green'],
    ['Twilio',      'SMS & WhatsApp',              'Connecté',      'green'],
    ['WooCommerce', 'Boutique en ligne',           'Non connecté',  'navy'],
    ['Shopify',     'Auto-import commandes',       'Non connecté',  'navy'],
    ['Google Maps', 'Routing & traffic',           'Connecté',      'green'],
    ['Slack',       'Alertes équipe',              'Connecté',      'green'],
    ['Zapier',      '+5,000 automatisations',      'Non connecté',  'navy'],
  ]

  const teamMembers = [
    ['Aymen Ben Ali',   'aymen@doordeal.tn',  'Super Admin',       'Maintenant'],
    ['Salma Mejri',     'salma@doordeal.tn',  'Operations Manager','5 min'],
    ['Karim Hammami',   'karim@doordeal.tn',  'Finance Manager',   '1h'],
    ['Sonia Trabelsi',  'sonia@doordeal.tn',  'Support',           '2h'],
    ['Mehdi Gharbi',    'mehdi@doordeal.tn',  'Read-only',         'Hier'],
  ]

  return (
    <>
      <div className="page-head">
        <div className="ph-titles">
          <h1>Paramètres</h1>
          <p>Configuration de l'application et préférences de l'équipe.</p>
        </div>
      </div>

      <div className="settings-grid">
        {/* Nav */}
        <div className="settings-nav">
          {sections.map(s => (
            <a key={s.id} className={page === s.id ? 'is-active' : ''} onClick={() => setPage(s.id)}>
              <DashIcon name={s.icon} size={14} style={{ verticalAlign: '-2px', marginRight: 8 }} />
              {s.label}
            </a>
          ))}
        </div>

        {/* Content */}
        <div>
          {page === 'general' && (
            <>
              <div className="settings-section">
                <h3>Informations entreprise</h3>
                <div className="section-desc">Identité légale et coordonnées de TariqGo.</div>
                <div className="detail-grid" style={{ '--detail-cols': '1fr 1fr', gap: 14 } as CSSProperties}>
                  <div className="field"><label>Raison sociale</label><input className="input" defaultValue="TariqGo SARL" /></div>
                  <div className="field"><label>Matricule fiscal</label><input className="input" defaultValue="1284562/A/M/000" /></div>
                  <div className="field"><label>Email contact</label><input className="input" defaultValue="doordeal.company@gmail.com" /></div>
                  <div className="field"><label>Téléphone</label><input className="input" defaultValue="+216 26 531 407" /></div>
                  <div className="field" style={{ gridColumn: '1 / -1' }}><label>Adresse siège</label><input className="input" defaultValue="Av. Habib Bourguiba, 1000 Tunis, Tunisie" /></div>
                </div>
              </div>
              <div className="settings-section">
                <h3>Préférences générales</h3>
                <div className="section-desc">Comportement par défaut de l'application.</div>
                <div className="settings-row">
                  <div className="row-text"><b>Mode sandbox</b><span>Active des données de test pour les développeurs.</span></div>
                  <Toggle on={tgls.sandbox} onClick={() => t('sandbox')} />
                </div>
                <div className="settings-row">
                  <div className="row-text"><b>Journal d'audit</b><span>Enregistre toutes les actions sensibles des admins.</span></div>
                  <Toggle on={tgls.auditLog} onClick={() => t('auditLog')} />
                </div>
                <div className="settings-row">
                  <div className="row-text"><b>Mises à jour temps-réel</b><span>WebSocket actif pour cartes et tableaux.</span></div>
                  <Toggle on={tgls.realtime} onClick={() => t('realtime')} />
                </div>
              </div>
            </>
          )}

          {page === 'operations' && (
            <div className="settings-section">
              <h3>Auto-assignation</h3>
              <div className="section-desc">Comment TariqGo sélectionne le bon livreur pour chaque commande.</div>
              <div className="settings-row">
                <div className="row-text"><b>Auto-assignation activée</b><span>Match automatique en moins de 8 secondes.</span></div>
                <Toggle on={tgls.autoAssign} onClick={() => t('autoAssign')} />
              </div>
              <div className="settings-row">
                <div className="row-text"><b>Surge pricing</b><span>Augmenter automatiquement le tarif en cas de pic de demande.</span></div>
                <Toggle on={tgls.surge} onClick={() => t('surge')} />
              </div>
              <div className="settings-row">
                <div className="row-text"><b>Rayon d'assignation maximum</b><span>Distance maximale entre livreur et point de pickup.</span></div>
                <input className="input" defaultValue="5 km" style={{ maxWidth: 120 }} />
              </div>
              <div className="settings-row">
                <div className="row-text"><b>Temps d'acceptation</b><span>Délai laissé au livreur avant de re-proposer.</span></div>
                <input className="input" defaultValue="20 sec" style={{ maxWidth: 120 }} />
              </div>
              <div className="settings-row">
                <div className="row-text"><b>Algorithme de matching</b><span>Quels critères utiliser pour matcher.</span></div>
                <select className="input" style={{ maxWidth: 240 }}>
                  <option>Distance + note (recommandé)</option>
                  <option>Distance uniquement</option>
                  <option>Round-robin équitable</option>
                  <option>Personnalisé</option>
                </select>
              </div>
            </div>
          )}

          {page === 'pricing' && (
            <>
              <div className="settings-section">
                <h3>SLA et qualité</h3>
                <div className="section-desc">Engagements de service et seuils d'alerte.</div>
                <div className="settings-row">
                  <div className="row-text"><b>SLA livraison standard</b><span>Délai cible pour livraison standard intra-ville.</span></div>
                  <input className="input" defaultValue="4 heures" style={{ maxWidth: 140 }} />
                </div>
                <div className="settings-row">
                  <div className="row-text"><b>SLA Express</b><span>Délai cible pour livraison express.</span></div>
                  <input className="input" defaultValue="1 heure" style={{ maxWidth: 140 }} />
                </div>
                <div className="settings-row">
                  <div className="row-text"><b>Seuil alerte SLA</b><span>Alerter quand un colis approche du dépassement.</span></div>
                  <input className="input" defaultValue="80% du SLA" style={{ maxWidth: 160 }} />
                </div>
                <div className="settings-row">
                  <div className="row-text"><b>Suspension auto livreur</b><span>Suspendre si note &lt; à la valeur sur 30 jours.</span></div>
                  <input className="input" defaultValue="4.0 ★" style={{ maxWidth: 120 }} />
                </div>
              </div>
              <div className="settings-section">
                <h3>Pricing par défaut</h3>
                <div className="section-desc">Tarifs appliqués si aucune règle de zone ne match.</div>
                <div className="detail-grid" style={{ '--detail-cols': '1fr 1fr 1fr', gap: 14 } as CSSProperties}>
                  <div className="field"><label>Tarif de base</label><input className="input" defaultValue="5.000 DT" /></div>
                  <div className="field"><label>Frais TariqGo</label><input className="input" defaultValue="12 %" /></div>
                  <div className="field"><label>Devise</label><select className="input"><option>TND (DT)</option><option>EUR (€)</option></select></div>
                </div>
              </div>
            </>
          )}

          {page === 'notifications' && (
            <div className="settings-section">
              <h3>Canaux de notification</h3>
              <div className="section-desc">Configurer quelles notifications partent et à qui.</div>
              <div className="settings-row">
                <div className="row-text"><b>SMS aux clients finaux</b><span>Lien de tracking + confirmation de livraison.</span></div>
                <Toggle on={tgls.sms} onClick={() => t('sms')} />
              </div>
              <div className="settings-row">
                <div className="row-text"><b>Emails transactionnels</b><span>Confirmations, reçus, alertes.</span></div>
                <Toggle on={tgls.email} onClick={() => t('email')} />
              </div>
              <div className="settings-row">
                <div className="row-text"><b>Push livreurs</b><span>Notifications mobiles pour les livreurs.</span></div>
                <Toggle on={tgls.push} onClick={() => t('push')} />
              </div>
              <div className="settings-row">
                <div className="row-text"><b>Provider SMS</b><span>Service utilisé pour l'envoi.</span></div>
                <select className="input" style={{ maxWidth: 200 }}>
                  <option>Tunisie Telecom Pro</option>
                  <option>Twilio</option>
                  <option>Orange Business</option>
                </select>
              </div>
            </div>
          )}

          {page === 'roles' && (
            <div className="settings-section">
              <h3>Équipe & rôles</h3>
              <div className="section-desc">Membres de l'équipe TariqGo et leurs permissions.</div>
              <div className="tbl-scroll">
              <table className="tbl" style={{ fontSize: '.85rem', marginBottom: 18 }}>
                <thead>
                  <tr><th>Membre</th><th>Email</th><th>Rôle</th><th>Dernière connexion</th><th className="col-actions">Actions</th></tr>
                </thead>
                <tbody>
                  {teamMembers.map((r, i) => (
                    <tr key={i}>
                      <td><div className="cell-user"><DashAvatar name={r[0]} size={28} /><b>{r[0]}</b></div></td>
                      <td className="mono" style={{ color: 'var(--muted)' }}>{r[1]}</td>
                      <td>
                        <span className={`badge ${
                          r[2].includes('Super')      ? 'badge-purple' :
                          r[2].includes('Operations') ? 'badge-green'  :
                          r[2].includes('Finance')    ? 'badge-blue'   :
                          r[2].includes('Support')    ? 'badge-amber'  : 'badge-navy'
                        }`}>{r[2]}</span>
                      </td>
                      <td style={{ color: 'var(--muted)' }}>{r[3]}</td>
                      <td className="col-actions"><button>Permissions</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              <button className="btn btn-primary"><DashIcon name="plus" size={14} /> Inviter un membre</button>
            </div>
          )}

          {page === 'integrations' && (
            <div className="settings-section">
              <h3>Intégrations</h3>
              <div className="section-desc">Connectez TariqGo à vos outils tiers.</div>
              {integrations.map((r, i) => (
                <div key={i} className="settings-row">
                  <div className="row-text"><b>{r[0]}</b><span>{r[1]}</span></div>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <span className={`badge badge-${r[3]}`}>{r[2]}</span>
                    <button className="btn btn-sm btn-ghost">{r[2] === 'Connecté' ? 'Configurer' : 'Connecter'}</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {page === 'security' && (
            <>
              <div className="settings-section">
                <h3>Sécurité du compte</h3>
                <div className="section-desc">Politique de sécurité de TariqGo.</div>
                <div className="settings-row">
                  <div className="row-text"><b>Authentification 2 facteurs (MFA)</b><span>Obligatoire pour tous les admins.</span></div>
                  <Toggle on={tgls.mfa} onClick={() => t('mfa')} />
                </div>
                <div className="settings-row">
                  <div className="row-text"><b>Durée de session</b><span>Déconnexion auto après inactivité.</span></div>
                  <select className="input" style={{ maxWidth: 200 }}>
                    <option>30 minutes</option><option>1 heure</option><option>4 heures</option><option>24 heures</option>
                  </select>
                </div>
                <div className="settings-row">
                  <div className="row-text"><b>IP allowlist</b><span>Restreindre l'admin à certaines IPs.</span></div>
                  <button className="btn btn-sm btn-ghost">Configurer · 0 IPs</button>
                </div>
              </div>
              <div className="settings-section">
                <h3>Politique mot de passe</h3>
                <div className="section-desc">Règles pour tous les utilisateurs internes.</div>
                <div className="settings-row"><div className="row-text"><b>Longueur minimale</b></div><input className="input" defaultValue="12 caractères" style={{ maxWidth: 160 }} /></div>
                <div className="settings-row"><div className="row-text"><b>Rotation</b></div><select className="input" style={{ maxWidth: 160 }}><option>90 jours</option><option>180 jours</option><option>Jamais</option></select></div>
              </div>
            </>
          )}

          {page === 'localization' && (
            <div className="settings-section">
              <h3>Localisation</h3>
              <div className="section-desc">Langue, fuseau et formats régionaux.</div>
              <div className="detail-grid" style={{ '--detail-cols': '1fr 1fr', gap: 14 } as CSSProperties}>
                <div className="field"><label>Langue par défaut</label><select className="input"><option>Français</option><option>English</option><option>العربية</option></select></div>
                <div className="field"><label>Fuseau horaire</label><select className="input"><option>Africa/Tunis (UTC+1)</option><option>UTC</option></select></div>
                <div className="field"><label>Format date</label><select className="input"><option>DD/MM/YYYY</option><option>YYYY-MM-DD</option></select></div>
                <div className="field"><label>Devise</label><select className="input"><option>TND (DT)</option><option>EUR (€)</option></select></div>
                <div className="field"><label>Système métrique</label><select className="input"><option>Kg / km</option><option>lb / mile</option></select></div>
                <div className="field"><label>Premier jour de la semaine</label><select className="input"><option>Lundi</option><option>Dimanche</option></select></div>
              </div>
            </div>
          )}

          {page === 'branding' && (
            <div className="settings-section">
              <h3>Identité</h3>
              <div className="section-desc">Logo, couleurs et apparence de l'application client.</div>
              <div className="settings-row">
                <div className="row-text"><b>Logo principal</b><span>Affiché en haut de l'app et sur les bordereaux.</span></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 48, height: 48, background: 'var(--navy-900)', borderRadius: 10, display: 'grid', placeItems: 'center' }}>
                    <img src="/assets/logo.png" alt="" style={{ maxWidth: 36, filter: 'brightness(0) invert(1)' }} />
                  </div>
                  <button className="btn btn-sm btn-ghost">Changer</button>
                </div>
              </div>
              <div className="settings-row">
                <div className="row-text"><b>Couleur primaire</b><span>Boutons et accents de marque.</span></div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ width: 32, height: 32, background: 'var(--navy-900)', borderRadius: 8, border: '2px solid #fff', boxShadow: '0 0 0 1px var(--line-2)', display: 'inline-block' }} />
                  <input className="input mono" defaultValue="#0A1E3A" style={{ maxWidth: 140 }} />
                </div>
              </div>
              <div className="settings-row">
                <div className="row-text"><b>Couleur d'accent</b><span>Confirmations, succès, CTA secondaires.</span></div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ width: 32, height: 32, background: 'var(--green-500)', borderRadius: 8, border: '2px solid #fff', boxShadow: '0 0 0 1px var(--line-2)', display: 'inline-block' }} />
                  <input className="input mono" defaultValue="#7CB342" style={{ maxWidth: 140 }} />
                </div>
              </div>
            </div>
          )}

          {page === 'billing' && (
            <div className="settings-section">
              <h3>Facturation TariqGo</h3>
              <div className="section-desc">Votre abonnement et historique de factures.</div>
              <div style={{ padding: 18, background: 'linear-gradient(135deg, var(--navy-900), var(--navy-700))', borderRadius: 14, color: '#fff', marginBottom: 18 }}>
                <div style={{ fontSize: '.78rem', opacity: .7, textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 600, marginBottom: 6 }}>Plan actuel</div>
                <div style={{ fontFamily: 'Space Grotesk', fontSize: '1.6rem', fontWeight: 700, marginBottom: 4 }}>Enterprise</div>
                <div style={{ opacity: .7, fontSize: '.86rem' }}>2,400 DT / mois · Illimité · Renouvellement le 31/05/2026</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                  <button className="wallet-card w-btn w-btn-primary" style={{ padding: '8px 14px', borderRadius: 9, background: 'var(--green-500)', color: '#fff', fontWeight: 600, fontSize: '.82rem', border: 'none' }}>Gérer le plan</button>
                  <button style={{ padding: '8px 14px', borderRadius: 9, background: 'rgba(255,255,255,.1)', color: '#fff', fontWeight: 600, fontSize: '.82rem', border: '1px solid rgba(255,255,255,.12)' }}>Annuler</button>
                </div>
              </div>
              <p style={{ color: 'var(--muted)', fontSize: '.85rem' }}>Factures précédentes disponibles dans <b style={{ color: 'var(--navy-900)' }}>Finances → Factures</b>.</p>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
